import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeftOutlined, DollarOutlined } from "@ant-design/icons";
import { Button, Card, Divider, InputNumber, Layout, Space, Typography, Alert, message } from "antd";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  completeCashAndPrintReceipt,
  goToScreen,
  selectCheckoutTotals,
  selectPosFlow,
  setCashReceived,
} from "../store/posSlice";
import { openCashDrawer } from "../store/settingsSlice";
import axiosClient from "../api/axiosClient";

const { Content } = Layout;
const { Title, Text } = Typography;

function formatMoney(n: number) {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function CashPaymentScreen() {
  const dispatch = useAppDispatch();
  const { cashReceived, changeDue, error } = useAppSelector(selectPosFlow);
  const { subtotal, tax, total, taxRate } = useAppSelector(selectCheckoutTotals);
  const [businessId, setBusinessId] = useState<string | null>(null);

  useEffect(() => {
    axiosClient.get("/business").then((res) => {
      if (res.data?.business_id) setBusinessId(res.data.business_id);
    }).catch(() => {});
  }, []);

  const canComplete = cashReceived >= total && total > 0;
  const quickAmounts = useMemo(() => {
    const near = Math.ceil(total); // 10.80 -> 11
    return [near, 20, 50];
  }, [total]);

  const handleCompleteCashSale = async () => {
    try {
      await dispatch(completeCashAndPrintReceipt()).unwrap();
      if (businessId) {
        try {
          await dispatch(openCashDrawer(businessId)).unwrap();
        } catch {
          // Cash drawer may not be configured for this business — the sale itself already succeeded.
        }
      }
    } catch (error) {
      message.error("Cash sale failed.");
    }
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      <div style={{ padding: 14, background: "#fff" }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => dispatch(goToScreen("payment_method"))}
        >
          Back
        </Button>
      </div>

      <Divider style={{ margin: 0 }} />

      <Content style={{ padding: 24 }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <div style={{ textAlign: "center", margin: "22px 0 22px" }}>
            <Text type="secondary">Amount Due</Text>
            <Title style={{ marginTop: 6, fontSize: 64, lineHeight: 1.05 }}>
              {formatMoney(total)}
            </Title>
            {taxRate > 0 && (
              <Text type="secondary">
                Subtotal {formatMoney(subtotal)} + Tax {formatMoney(tax)}
              </Text>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "center" }}>
            <Card style={{ width: "min(820px, 92vw)", borderRadius: 16 }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 999,
                    background: "#dcfce7",
                    color: "#16a34a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 22,
                  }}
                >
                  <DollarOutlined />
                </div>
              </div>

              <Title level={4} style={{ textAlign: "center" }}>
                Cash Received
              </Title>

              <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
                <InputNumber
                  value={cashReceived}
                  onChange={(v) => dispatch(setCashReceived(typeof v === "number" ? v : 0))}
                  min={0}
                  step={0.01}
                  precision={2}
                  controls
                  prefix={<span style={{ color: "rgba(0,0,0,0.35)", fontWeight: 500 }}>$</span>}
                  size="large"
                  style={{
                    width: "min(720px, 86vw)",
                    borderRadius: 14,
                  }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
                <Space size={16}>
                  {quickAmounts.map((amt) => (
                    <Button
                      key={amt}
                      size="large"
                      style={{ minWidth: 170, height: 58, borderRadius: 14 }}
                      onClick={() => dispatch(setCashReceived(amt))}
                    >
                      {formatMoney(amt)}
                    </Button>
                  ))}
                </Space>
              </div>

              <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
                <div style={{ width: "min(720px, 86vw)" }}>
                  {cashReceived > 0 && (
                    <Alert
                      type={canComplete ? "success" : "error"}
                      showIcon
                      message={canComplete ? "Change Due" : "Additional Amount Needed"}
                      description={
                        <Text strong style={{ fontSize: 18 }}>
                          {formatMoney(canComplete ? changeDue : total - cashReceived)}
                        </Text>
                      }
                    />
                  )}
                </div>
              </div>

              {error && (
                <div style={{ marginTop: 12 }}>
                  <Alert type="error" showIcon message={error} />
                </div>
              )}
            </Card>
          </div>

          <div
            style={{
              marginTop: 18,
              display: "flex",
              justifyContent: "center",
              gap: 12,
            }}
          >
            <Button
              type="primary"
              size="large"
              disabled={!canComplete}
              style={{
                width: "min(640px, 70vw)",
                height: 56,
                borderRadius: 14,
                fontWeight: 700,
              }}
              onClick={() => handleCompleteCashSale()}
            >
              Complete Payment
            </Button>

            <Button
              size="large"
              style={{ width: 140, height: 56, borderRadius: 14, fontWeight: 600 }}
              onClick={() => dispatch(goToScreen("payment_method"))}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Content>
    </Layout>
  );
}